import produce from "immer";
import { head, isEmpty, keyBy, keys, orderBy, values, without } from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ApiResponse, fetchApi } from "../../utils/api";
import timer from "../../utils/timer";
import { PlanType, Workspace } from "../../utils/types";
import usePrevious from "../../utils/usePrevious";
import { useAuthentication } from "../authentication";
import { useToast } from "../toast";
import useWorkspaceState from "./useWorkspaceState";
import WorkspaceFormSidebar from "./WorkspaceFormSidebar";

interface WorkspacesMap {
  [workspaceId: string]: Workspace;
}

export type ContextValue = {
  workspaces: WorkspacesMap;
  loadWorkspaces: () => Promise<void>;
  setWorkspaces: React.Dispatch<React.SetStateAction<WorkspacesMap>>;
  fetchWorkspaceById: (workspaceUuid?: string) => Promise<void>;
  currentWorkspace: Workspace | null;
  currentWorkspaceIsLocked: boolean | undefined;
  currentWorkspaceId: string | undefined;
  setCurrentWorkspaceId: (workspaceId: string) => void;
  toggleCreateWorkspaceForm: () => void;
  updateWorkspace: (
    updatedWorkspaceValues: Partial<Workspace>
  ) => Promise<ApiResponse<Workspace> | void>;
  currentWorkspaceIsDowngradePlan: boolean;
  currentWorkspaceIsDowngradePrePaidPlan: boolean;
  currentWorkspaceIsDowngradePrePaidHotspot: boolean;
  currentWorkspaceIsDowngradePrePaidSimulation: boolean;
  deleteWorkspace: () => Promise<void>;
  currentWorkspaceAllowedForCommunityPlan: boolean;
};

export const WorkspacesContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

export const WorkspacesProvider: React.FC<Props> = ({
  children,
  ...otherProps
}) => {
  const { addToast } = useToast();
  const { user, authenticationState, logout, openChat } = useAuthentication();
  const [workspaces, setWorkspaces] = useState<WorkspacesMap>({});
  const { search, hash } = useLocation();
  const history = useHistory();
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<
    string | undefined
  >();
  const lastWorkspaceId = useWorkspaceState(
    (state) => state.currentWorkspaceId
  );
  const setLastWorkspaceId = useWorkspaceState(
    (state) => state.setCurrentWorkspaceId
  );
  const [openWorkspaceFormSidebar, setOpenWorkspaceFormSidebar] =
    useState(false);

  const toggleCreateWorkspaceForm = useCallback(
    () => setOpenWorkspaceFormSidebar((prevValue) => !prevValue),
    []
  );

  const communityWorkspaces = useMemo(() => {
    const communityWorkspaces = values(workspaces).filter(
      (workspace) => workspace.active_plan?.type === PlanType.community
    );
    return orderBy(
      communityWorkspaces,
      [(workspace) => moment(workspace.created_at).valueOf()],
      ["asc"]
    );
  }, [workspaces]);

  const loadWorkspaces = useCallback(async () => {
    const response = await fetchApi("/api/workspaces");

    const apiResponse: ApiResponse<Workspace[]> = await response.json();

    if (!apiResponse.status) return;

    const newWorkspaces = keyBy(apiResponse.data, "uuid");

    setWorkspaces(newWorkspaces);

    if (currentWorkspaceId) return;

    if (lastWorkspaceId && newWorkspaces[lastWorkspaceId]) {
      setCurrentWorkspaceId(lastWorkspaceId);
      return;
    }

    if (!isEmpty(newWorkspaces)) {
      setCurrentWorkspaceId((head(apiResponse.data) as Workspace).uuid);
      return;
    }
  }, [currentWorkspaceId, lastWorkspaceId]);

  const fetchWorkspaceById = useCallback(async (workspaceUuid?: string) => {
    if (!workspaceUuid) return;

    const timerResponse = await timer(5000);

    const response = await fetchApi(`/api/workspace/${workspaceUuid}`);

    clearTimeout(timerResponse);

    const apiResponse: ApiResponse<Workspace> = await response.json();

    if (!apiResponse.status) return;

    setWorkspaces((prevState) => ({
      ...prevState,
      [workspaceUuid]: apiResponse.data
    }));
  }, []);

  const prevUser = usePrevious(user);
  useEffect(() => {
    if (
      currentWorkspaceId &&
      prevUser !== user &&
      user?.workspaces &&
      !user.workspaces.includes(currentWorkspaceId)
    ) {
      const newWorkspaceUuid =
        head(without(keys(workspaces), currentWorkspaceId)) || "";
      setCurrentWorkspaceId(newWorkspaceUuid);
      addToast({
        type: "warning",
        title: "You can no longer access this workspace"
      });
      loadWorkspaces();
    }
  }, [
    addToast,
    currentWorkspaceId,
    loadWorkspaces,
    prevUser,
    user,
    workspaces
  ]);

  const currentWorkspace = useMemo(
    () => (currentWorkspaceId ? workspaces[currentWorkspaceId] : null),
    [currentWorkspaceId, workspaces]
  );

  const currentWorkspaceIsLocked = useMemo(
    () => currentWorkspace?.active_plan.status === "past_due",
    [currentWorkspace?.active_plan.status]
  );

  const currentWorkspaceIsDowngradePlan = useMemo(
    () =>
      currentWorkspace?.downgrade?.current?.plan?.id !==
      currentWorkspace?.downgrade?.future?.plan?.id,
    [currentWorkspace?.downgrade]
  );

  const currentWorkspaceIsDowngradePrePaidHotspot = useMemo(
    () =>
      currentWorkspace?.downgrade?.current?.["pre-paid"]?.hotspot?.quantity !==
      currentWorkspace?.downgrade?.future?.["pre-paid"]?.hotspot?.quantity,
    [currentWorkspace?.downgrade]
  );

  const currentWorkspaceIsDowngradePrePaidSimulation = useMemo(
    () =>
      currentWorkspace?.downgrade?.current?.["pre-paid"]?.simulation
        ?.quantity !==
      currentWorkspace?.downgrade?.future?.["pre-paid"]?.simulation?.quantity,
    [currentWorkspace?.downgrade]
  );

  const currentWorkspaceAllowedForCommunityPlan = useMemo(() => {
    if (
      communityWorkspaces.length > 1 &&
      currentWorkspace?.active_plan.type === PlanType.community
    ) {
      return head(communityWorkspaces)?.uuid === currentWorkspace?.uuid;
    }

    return true;
  }, [
    communityWorkspaces,
    currentWorkspace?.active_plan.type,
    currentWorkspace?.uuid
  ]);

  const currentWorkspaceIsDowngradePrePaidPlan = useMemo(
    () =>
      currentWorkspaceIsDowngradePrePaidHotspot ||
      currentWorkspaceIsDowngradePrePaidSimulation,
    [
      currentWorkspaceIsDowngradePrePaidHotspot,
      currentWorkspaceIsDowngradePrePaidSimulation
    ]
  );

  useEffect(() => {
    if (!currentWorkspaceIsLocked) return;

    addToast({
      type: "warning",
      waitForInteraction: true,
      title: (
        <span>
          Your subscription payment failed. Please resolve this issue by{" "}
          {currentWorkspace?.active_plan.requires_action_link && (
            <a
              className="font-semibold text-hotspotty-300 cursor-pointer"
              href={currentWorkspace?.active_plan.requires_action_link}
              target="_blank"
              rel="noreferrer"
            >
              clicking here
            </a>
          )}
          {currentWorkspace?.active_plan.requires_action === "authentication" &&
            !currentWorkspace?.active_plan.requires_action_link && (
              <span>
                Code 1430 -{" "}
                <b
                  onClick={() => openChat()}
                  className="font-semibold text-hotspotty-300 cursor-pointer"
                >
                  Please contact support team
                </b>
              </span>
            )}
          .
        </span>
      )
    });
  }, [
    addToast,
    currentWorkspace?.active_plan.requires_action,
    currentWorkspace?.active_plan.requires_action_link,
    currentWorkspaceIsLocked,
    history,
    openChat
  ]);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const workspace =
      searchParams.get("workspace") ||
      sessionStorage.getItem("lastWorkspaceBySearch");
    const hashParam = hash || sessionStorage.getItem("lastHash") || "";

    if (!workspace) return;

    sessionStorage.setItem("lastWorkspaceBySearch", workspace);
    sessionStorage.setItem("lastHash", hashParam);

    if (!workspaces[workspace] || currentWorkspaceId === workspace) {
      return;
    }

    setCurrentWorkspaceId(workspace);

    searchParams.delete("workspace");
    history.replace({
      hash: hashParam || "",
      search: searchParams.toString()
    });

    sessionStorage.removeItem("lastWorkspaceBySearch");
    sessionStorage.removeItem("lastHash");
  }, [currentWorkspaceId, hash, history, search, workspaces]);

  const updateWorkspace = useCallback(
    async (updatedValues: Partial<Workspace>) => {
      if (!currentWorkspace) return;

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}`,
          {
            method: "PUT",
            body: JSON.stringify({
              name: currentWorkspace.name,
              ...updatedValues
            })
          }
        );

        addToast({
          type: "success",
          message: "Updated successfully"
        });

        const apiResponse: ApiResponse<Workspace> = await response.json();

        if (apiResponse.status) {
          setWorkspaces((prevWorkspaces) =>
            produce(prevWorkspaces, (draft) => {
              draft[apiResponse.data.uuid].name = apiResponse.data.name;
            })
          );
        }

        return apiResponse;
      } catch {
        addToast({
          type: "error",
          message: "Error updating value"
        });
        throw Error("Something went wrong");
      }
    },
    [addToast, currentWorkspace, currentWorkspaceId]
  );

  useEffect(() => {
    if (authenticationState !== "authenticated" && currentWorkspaceId) {
      setCurrentWorkspaceId(undefined);
    }

    if (authenticationState !== "authenticated" || currentWorkspaceId) return;

    loadWorkspaces();
  }, [authenticationState, currentWorkspaceId, loadWorkspaces]);

  useEffect(() => {
    if (!currentWorkspaceId || currentWorkspaceId === lastWorkspaceId) return;

    setLastWorkspaceId(currentWorkspaceId);
  }, [currentWorkspaceId, lastWorkspaceId, setLastWorkspaceId]);

  const addWorkspace = useCallback(
    async (name: string) => {
      const response = await fetchApi("/api/workspaces", {
        method: "POST",
        body: JSON.stringify({ name })
      });

      const apiResponse: ApiResponse<Workspace> = await response.json();

      await loadWorkspaces();

      if (apiResponse.status) {
        setCurrentWorkspaceId(apiResponse.data.uuid);
      }

      return apiResponse;
    },
    [loadWorkspaces]
  );

  const deleteWorkspace = useCallback(async () => {
    if (!currentWorkspace) return;

    try {
      const response = await fetchApi(`/api/workspace/${currentWorkspaceId}`, {
        method: "DELETE"
      });

      addToast({
        type: "success",
        message: "Workspace deleted successfully"
      });

      const apiResponse: ApiResponse<Workspace> = await response.json();

      if (apiResponse.status) {
        logout();
      }
    } catch {
      addToast({
        type: "error",
        message: "Error deleting workspace"
      });
      throw Error("Something went wrong deleting this workspace");
    }
  }, [addToast, currentWorkspace, currentWorkspaceId, logout]);

  const value = useMemo(
    () => ({
      workspaces,
      loadWorkspaces,
      currentWorkspaceId,
      setCurrentWorkspaceId,
      toggleCreateWorkspaceForm,
      updateWorkspace,
      currentWorkspace,
      setWorkspaces,
      currentWorkspaceIsLocked,
      currentWorkspaceIsDowngradePlan,
      deleteWorkspace,
      currentWorkspaceIsDowngradePrePaidPlan,
      currentWorkspaceIsDowngradePrePaidSimulation,
      currentWorkspaceIsDowngradePrePaidHotspot,
      currentWorkspaceAllowedForCommunityPlan,
      fetchWorkspaceById
    }),
    [
      workspaces,
      loadWorkspaces,
      currentWorkspaceId,
      setCurrentWorkspaceId,
      toggleCreateWorkspaceForm,
      updateWorkspace,
      currentWorkspace,
      setWorkspaces,
      currentWorkspaceIsLocked,
      currentWorkspaceIsDowngradePlan,
      deleteWorkspace,
      currentWorkspaceIsDowngradePrePaidPlan,
      currentWorkspaceIsDowngradePrePaidSimulation,
      currentWorkspaceIsDowngradePrePaidHotspot,
      currentWorkspaceAllowedForCommunityPlan,
      fetchWorkspaceById
    ]
  );

  return (
    <WorkspacesContext.Provider value={value} {...otherProps}>
      {children}

      <WorkspaceFormSidebar
        open={openWorkspaceFormSidebar}
        toggleCreateWorkspaceForm={toggleCreateWorkspaceForm}
        addWorkspace={addWorkspace}
      />
    </WorkspacesContext.Provider>
  );
};

export const useWorkspaces = (): ContextValue => {
  const context = useContext(WorkspacesContext);
  if (context === undefined) {
    throw new Error("useWorkspaces must be used within an WorkspacesProvider");
  }
  return context;
};
